import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  doc,
  onSnapshot,
  collection,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import { db } from "../services/firebase";
import { toJsDate } from "../utils/toJsDate";
import {
  useParams,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import {
  hydrateActivities,
  useActivityActions,
  useActivityFeedData,
} from "../contexts/ActivityContext";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import { type ActivityRequest } from "../types";
import ActivityCard from "../components/ActivityCard";
import EditActivityModal from "../components/EditActivityModal";
import ReportActivityModal from "../components/ReportActivityModal";
import AuditLogItem from "../components/AuditLogItem";
import ConfirmModal from "../components/ConfirmModal";
import InviteUsersModal from "../components/InviteUsersModal";
import { usePageTracking } from "../hooks/usePageTracking";
import {
  createMatchNavigationTarget,
  resolveActivityJoinError,
  resolveActivityJoinFailure,
  resolveInviteResponseSuccess,
} from "../utils/pageNavigationFlows";
import { CommonFunctions } from "../utils/commonUtils";
import { useCachedImagesArray } from "../hooks/useCachedImagesArray";
import { App as CapacitorApp } from "@capacitor/app";
import { Device } from "@capacitor/device";
import { KeyboardHandler } from "../utils/keyboardHandler";
import { ActivityRichText } from "../utils/activityRichText";

import {
  ArrowLeftIcon,
  UsersIcon,
  MessageSquareIcon,
  UserPlusIcon,
  LogOutIcon,
  Trash2Icon,
  Edit2Icon,
  LinkIcon,
  MapPinIcon,
  ClockIcon,
  CalendarIcon,
  UserXIcon,
  RefreshCwIcon,
} from "../components/icons/FeatherIcons";
import { reportActivity as reportActivityCallable } from "../services/functionsApi";
import { Capacitor } from "@capacitor/core";
import { createPortal } from "react-dom";

import { crashlytics } from "../services/firebase";
import { nav } from "framer-motion/client";
interface PortalProps {
  children: React.ReactNode;
}

const Portal: React.FC<PortalProps> = ({ children }) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  return mounted ? createPortal(children, document.body) : null;
};

const ActivityPage: React.FC = () => {
  usePageTracking("Activity", "ActivityPage");
  const { activityId } = useParams<{ activityId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, getUsersByIds } = useAuth();
  const { addToast } = useToast();
  const { activities } = useActivityFeedData();
  const {
    joinActivity,
    leaveActivity,
    deleteActivity,
    updateActivity,
    refreshSingleActivity,
    kickParticipant,
    approveRejoinRequest,
    declineRejoinRequest,
    inviteUsersToActivity,
    cancelInvite,
    respondToInvite,
  } = useActivityActions();

  // Persist active tab in sessionStorage
  const getInitialSection = () => {
    const saved = sessionStorage.getItem("activityPageActiveSection");
    if (saved === "details" || saved === "participants" || saved === "activity")
      return saved;
    return "details";
  };

  const [activeSection, setActiveSection] = useState<
    "details" | "participants" | "activity"
  >(getInitialSection());
  const contentRef = useRef<HTMLDivElement>(null);
  const [isJoining, setIsJoining] = useState(false);
  const [isLoadingActivity, setIsLoadingActivity] = useState(false);
  const [isRespondingToInvite, setIsRespondingToInvite] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    confirmText?: string;
    confirmStyle?: "danger" | "primary";
  }>({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
  });

  // Local state for single activity
  const routeState = location.state as
    | { prefetchedActivity?: ActivityRequest }
    | undefined;
  const prefetchedActivity =
    routeState?.prefetchedActivity?.id === activityId
      ? routeState.prefetchedActivity
      : null;
  const localActivity = activities.find((a) => a.id === activityId) || null;
  const initialActivity = prefetchedActivity || localActivity;

  const [activity, setActivity] = useState<ActivityRequest | null>(initialActivity);
  const [activityError, setActivityError] = useState<string | null>(null);
  const [pendingRejoinUsers, setPendingRejoinUsers] = useState<any[]>([]);
  const [pendingInviteUsers, setPendingInviteUsers] = useState<any[]>([]);

  // Memoize participants array to prevent infinite update loops
  const memoParticipants = useMemo(
    () =>
      activity && Array.isArray(activity.participants)
        ? activity.participants
        : [],
    [activity?.participants],
  );
  // Always call the hook at the top level, not conditionally
  const participantImages = useCachedImagesArray(memoParticipants);
  const pendingInviteImages = useCachedImagesArray(pendingInviteUsers);
  const pendingRejoinImages = useCachedImagesArray(pendingRejoinUsers);

  // Real-time listener for single activity
  useEffect(() => {
    if (!activityId) return;
    setActivityError(null);
    const hasImmediateData =
      (activity?.id === activityId ? activity : null) ||
      prefetchedActivity ||
      localActivity;

    if (!hasImmediateData) {
      setIsLoadingActivity(true);
    } else {
      setActivity(hasImmediateData);
      setIsLoadingActivity(false);
    }

    const activityRef = doc(db, "activities", activityId);
    const unsub = onSnapshot(
      activityRef,
      async (snapshot) => {
        if (!snapshot.exists()) {
          setActivity(null);
          setActivityError("Activity not found.");
          setIsLoadingActivity(false);
          return;
        }
        const docData = snapshot.data();
        // Hydrate with user objects
        try {
          const hydrated = await hydrateActivities(
            [{ id: snapshot.id, ...docData }],
            getUsersByIds,
          );
          setActivity(hydrated[0] || null);
          setActivityError(null);
        } catch (err) {
          setActivity(null);
          setActivityError("Failed to hydrate activity.");
        }
        setIsLoadingActivity(false);
      },
      (error) => {
        setActivityError("Failed to load activity.");
        setIsLoadingActivity(false);
      },
    );
    return () => unsub();
  }, [activityId, getUsersByIds, prefetchedActivity, localActivity]);

  useEffect(() => {
    if (isLoadingActivity && Capacitor.isNativePlatform()) {
      KeyboardHandler.getInstance().hideKeyboard();
    }
  }, [isLoadingActivity]);

  // Check section from URL params and scroll to it
  useEffect(() => {
    const section = searchParams.get("section");
    if (section && ["details", "participants", "activity"].includes(section)) {
      setActiveSection(section as "details" | "participants" | "activity");
      sessionStorage.setItem("activityPageActiveSection", section);
      setTimeout(() => {
        if (contentRef.current) {
          contentRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 100);
    }
  }, [searchParams]);

  // Save tab on change
  useEffect(() => {
    sessionStorage.setItem("activityPageActiveSection", activeSection);
  }, [activeSection]);

  // Real-time listener for audit logs
  const [auditLogs, setAuditLogs] = useState([]);
  useEffect(() => {
    if (!activityId) return;
    const auditLogsRef = collection(db, "auditLogs");
    const q = query(
      auditLogsRef,
      where("link", "==", `/activity/${activityId}`),
      orderBy("createdAt", "desc"),
    );
    const unsub = onSnapshot(q, (snapshot) => {
      const logs = snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      setAuditLogs(logs);
    });
    return () => unsub();
  }, [activityId]);

  // Fetch pending rejoin request users
  useEffect(() => {
    let cancelled = false;
    const fetchPendingRejoinUsers = async () => {
      if (
        activity &&
        activity.pendingRejoinRequests &&
        activity.pendingRejoinRequests.length > 0
      ) {
        try {
          const users = await getUsersByIds(activity.pendingRejoinRequests);
          if (!cancelled) {
            setPendingRejoinUsers(users);
          }
        } catch (error) {
          crashlytics.logError(
            `Error fetching pending rejoin users: ${JSON.stringify(error)}`,
            "ActivityPage.tsx",
            error,
          );
        }
      } else {
        setPendingRejoinUsers([]);
      }
    };
    fetchPendingRejoinUsers();
    return () => {
      cancelled = true;
    };
  }, [activity?.pendingRejoinRequests, getUsersByIds]);

  // Fetch pending invite users
  useEffect(() => {
    let cancelled = false;
    const fetchPendingInviteUsers = async () => {
      if (
        activity &&
        activity.pendingInvites &&
        activity.pendingInvites.length > 0
      ) {
        try {
          const users = await getUsersByIds(activity.pendingInvites);
          if (!cancelled) {
            setPendingInviteUsers(users);
          }
        } catch (error) {
          crashlytics.logError(
            `Error fetching pending invite users: ${JSON.stringify(error)}`,
            "ActivityPage.tsx",
            error,
          );
        }
      } else {
        setPendingInviteUsers([]);
      }
    };
    fetchPendingInviteUsers();
    return () => {
      cancelled = true;
    };
  }, [activity?.pendingInvites, getUsersByIds]);

  if (!activityId) {
    return (
      <div className="bg-white min-h-screen">
        <div className="w-full max-w-md md:max-w-2xl lg:max-w-4xl mx-auto pt-8">
          <div className="p-4 md:p-8 text-center">
            <h1 className="text-xl md:text-2xl font-bold text-red-600">
              Activity Not Found
            </h1>
            <p className="text-gray-600 mt-2">Invalid activity ID</p>
            <button
              onClick={() => navigate("/")}
              className="mt-4 bg-primary text-white px-6 py-2 rounded-full hover:bg-primary/90 transition-colors"
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoadingActivity) {
    return (
      <div className="bg-white min-h-screen">
        <div className="w-full max-w-md md:max-w-2xl lg:max-w-4xl mx-auto">
          <div className="flex items-center p-4 md:p-6 border-b border-gray-200">
            <button onClick={() => navigate(-1)} className="text-gray-600 mr-4">
              <ArrowLeftIcon className="w-6 h-6" />
            </button>
            <h1 className="text-lg md:text-xl font-semibold">Activity</h1>
          </div>
          <div className="p-4 md:p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 md:h-16 md:w-16 border-b-2 border-primary mx-auto mb-4"></div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-600">
              Loading Activity...
            </h2>
            <p className="text-gray-500 mt-2">
              Please wait while we fetch the activity details.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!activity || activityError) {
    return (
      <div className="bg-white min-h-screen">
        <div className="w-full max-w-md md:max-w-2xl lg:max-w-4xl mx-auto">
          <div className="flex items-center p-4 md:p-6 border-b border-gray-200">
            <button onClick={() => navigate(-1)} className="text-gray-600 mr-4">
              <ArrowLeftIcon className="w-6 h-6" />
            </button>
            <h1 className="text-lg md:text-xl font-semibold">Activity</h1>
          </div>
          <div className="p-4 md:p-8 text-center">
            <h2 className="text-xl md:text-2xl font-bold text-gray-600">
              Activity Not Found
            </h2>
            <p className="text-gray-500 mt-2">
              This activity may have been deleted or doesn't exist.
            </p>
            <button
              onClick={() => navigate("/")}
              className="mt-4 bg-primary text-white px-6 py-2 rounded-full hover:bg-primary/90 transition-colors"
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Compute participant, creator, full, and expired status safely
  const isParticipant = activity
    ? activity.participants.some((p) => p.id === currentUser?.id)
    : false;
  const isCreator = activity ? activity.creator.id === currentUser?.id : false;
  const isFull = activity
    ? activity.participants.length >= activity.groupSize
    : false;
  const isExpired = activity
    ? (() => {
        const now = new Date().getTime();
        const endTime =
          toJsDate(activity.dateTime).getTime() + activity.duration * 60 * 1000;
        return endTime < now;
      })()
    : false;
  const hasPendingRejoinRequest =
    activity?.pendingRejoinRequests?.includes(currentUser?.id || "") || false;
  const hasPendingInvite =
    activity?.pendingInvites?.includes(currentUser?.id || "") || false;

  const handleJoin = async () => {
    if (!currentUser || isJoining) return;

    if (activity?.isReported) {
      addToast("Cannot join an activity that is under review", "error");
      return;
    }

    if (isExpired) {
      addToast("Cannot join an expired activity", "error");
      return;
    }

    setIsJoining(true);
    const matchNavigation = createMatchNavigationTarget(activity.id, activity);
    navigate(matchNavigation.path, {
      state: matchNavigation.state,
    });

    void joinActivity(activity.id)
      .then((result) => {
        if (result.success) {
          return;
        }

        const failure = resolveActivityJoinFailure(activity.id);
        addToast(failure.toast.message, failure.toast.type);
        navigate(failure.navigation.path, { replace: failure.navigation.replace });
      })
      .catch((error) => {
        crashlytics.logError(
          `Error joining activity: ${JSON.stringify(error?.message)}`,
          "ActivityPage.tsx",
          error,
        );
        const failure = resolveActivityJoinError(activity.id);
        addToast(failure.toast.message, failure.toast.type);
        navigate(failure.navigation.path, { replace: failure.navigation.replace });
      })
      .finally(() => {
        setIsJoining(false);
      });
  };

  const handleLeave = async () => {
    if (!currentUser) return;

    try {
      await leaveActivity(activity.id);
      // No need to refresh - leaveActivity updates local state optimistically
      addToast(`${currentUser.username} has left the activity.`, "info");
      navigate("/profile", { state: { initialTab: "declined" } });
    } catch (error) {
      crashlytics.logError(
        `Error leaving activity: ${JSON.stringify(error?.message)}`,
        "ActivityPage.tsx",
        error,
      );
      addToast("An error occurred while leaving the activity.", "error");
    }
  };

  const handleDelete = async () => {
    if (!currentUser || !isCreator) return;

    if (isExpired) {
      addToast("Cannot delete an expired activity", "error");
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: "Delete Activity",
      message:
        "Are you sure you want to delete this activity? This action cannot be undone.",
      confirmText: "Delete",
      confirmStyle: "danger",
      onConfirm: async () => {
        try {
          await deleteActivity(activity.id);
          navigate("/profile", { state: { initialTab: "deleted" } });
        } catch (error) {
          crashlytics.logError(
            `Error deleting activity: ${JSON.stringify(error?.message)}`,
            "ActivityPage.tsx",
            error,
          );
          addToast("An error occurred while deleting the activity.", "error");
        }
        setConfirmModal((prev) => ({ ...prev, isOpen: false }));
      },
    });
  };

  const handleChat = () => {
    // Navigate to chat with readOnly parameter if activity is expired
    const readOnlyParam = isExpired ? "?readonly=true" : "";
    navigate(`/chat/${activity.chatId}${readOnlyParam}`);
  };

  const handleEdit = () => {
    if (!isCreator || !activity) return;
    setShowEditModal(true);
  };

  const handleSaveEdit = async (updatedActivity: ActivityRequest) => {
    try {
      await updateActivity(updatedActivity);
      setShowEditModal(false);
      addToast("Activity updated successfully!", "success");
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : typeof error === "string"
            ? error
            : JSON.stringify(error);
      crashlytics.logError(
        `Error updating activity: ${errorMessage}`,
        "ActivityPage.tsx",
        error,
      );
      addToast("Failed to update activity. Please try again.", "error");
    }
  };

  const handleAcceptInvite = async () => {
    if (!currentUser || !activity || isRespondingToInvite) return;

    setIsRespondingToInvite(true);
    try {
      await respondToInvite(activity.id, true);
      const outcome = resolveInviteResponseSuccess(activity.id, true);
      addToast(outcome.toast.message, outcome.toast.type);
      setTimeout(() => {
        navigate(outcome.navigation.path);
      }, outcome.delayMs);
    } catch (error) {
      crashlytics.logError(
        `Error accepting invite: ${JSON.stringify(error?.message)}`,
        "ActivityPage.tsx",
        error,
      );
      addToast("Failed to accept invitation. Please try again.", "error");
    } finally {
      setIsRespondingToInvite(false);
    }
  };

  const handleDeclineInvite = async () => {
    if (!currentUser || !activity || isRespondingToInvite) return;

    setIsRespondingToInvite(true);
    try {
      await respondToInvite(activity.id, false);
      const outcome = resolveInviteResponseSuccess(activity.id, false);
      addToast(outcome.toast.message, outcome.toast.type);
      setTimeout(() => {
        navigate(outcome.navigation.path);
      }, outcome.delayMs);
    } catch (error) {
      crashlytics.logError(
        `Error declining invite: ${JSON.stringify(error?.message)}`,
        "ActivityPage.tsx",
        error,
      );
      addToast("Failed to decline invitation. Please try again.", "error");
    } finally {
      setIsRespondingToInvite(false);
    }
  };

  const handleInvite = async (userIds: string[]) => {
    if (!activity) return;
    if (activity.participants.length >= activity.groupSize) {
      addToast("This activity is already full.", "error");
      return;
    }
    try {
      await inviteUsersToActivity(activity.id, userIds);
      setShowInviteModal(false);
    } catch (error) {
      crashlytics.logError(
        `Error inviting users: ${JSON.stringify(error?.message)}`,
        "ActivityPage.tsx",
        error,
      );
      addToast("Failed to invite users. Please try again.", "error");
    }
  };

  const handleShare = async () => {
    if (!activity) return;

    try {
      // Use smart redirect page that will:
      // 1. Try to open in app if installed
      // 2. Offer to download from app store if not
      // 3. Allow viewing in browser as fallback
      const shareUrl = `https://app.whoisin.sg/app-redirect.html?activity=${activity.id}`;
      const shareText = `Check out this activity: ${activity.title}`;

      // Try Capacitor Share plugin first (better for mobile apps)
      try {
        const { Share } = await import("@capacitor/share");
        await Share.share({
          title: activity.title,
          text: shareText,
          url: shareUrl,
          dialogTitle: "Share activity",
        });
      } catch (capacitorError) {
        if (
          capacitorError?.message !== "Share canceled" &&
          capacitorError?.name !== "AbortError"
        ) {
          // Fallback to Web Share API (works in mobile browsers)
          if (navigator.share) {
            await navigator.share({
              title: activity.title,
              text: shareText,
              url: shareUrl,
            });
          } else {
            if (navigator.clipboard && navigator.clipboard.writeText) {
              // Final fallback: copy to clipboard
              await navigator.clipboard.writeText(shareUrl);
              addToast("Link copied to clipboard!", "success");
            }
          }
        }
      }
    } catch (error: any) {
      // User cancelled or error occurred
      if (error?.message !== "Share canceled" && error?.name !== "AbortError") {
        crashlytics.logError(
          `Error sharing activity: ${JSON.stringify(error?.message)}`,
          "ActivityPage.tsx",
          error,
        );
        addToast("Failed to share activity", "error");
      }
    }
  };

  const handleKickParticipant = async (
    participantId: string,
    participantName: string,
  ) => {
    if (activity?.isReported) {
      addToast(
        "Cannot remove participants while activity is under review",
        "error",
      );
      return;
    }

    if (isExpired) {
      addToast("Cannot remove participants from an expired activity", "error");
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: "Remove Participant",
      message: `Are you sure you want to remove ${participantName} from this activity?`,
      confirmText: "Remove",
      confirmStyle: "danger",
      onConfirm: async () => {
        try {
          await kickParticipant(activityId, participantId);
          addToast(
            `${participantName} has been removed from the activity`,
            "success",
          );
        } catch (error) {
          crashlytics.logError(
            `Error removing participant: ${JSON.stringify(error?.message)}`,
            "ActivityPage.tsx",
            error,
          );
          addToast("Failed to remove participant", "error");
        }
        setConfirmModal((prev) => ({ ...prev, isOpen: false }));
      },
    });
  };

  const handleManualRefresh = async () => {
    if (isRefreshing) return;

    setIsRefreshing(true);
    try {
      await refreshSingleActivity(activityId);
      addToast("Activity updated!", "success");
    } catch (error) {
      crashlytics.logError(
        `Error refreshing activity: ${JSON.stringify(error?.message)}`,
        "ActivityPage.tsx",
        error,
      );
      addToast("Failed to refresh activity", "error");
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleReportSubmit = async (reason: string) => {
    if (!currentUser || !activity) return;

    try {
      // Collect device info if available
      let deviceInfo: Record<string, string | undefined> = {
        userAgent: navigator.userAgent,
      };

      try {
        const cap = (window as any).Capacitor || undefined;
        if (cap && cap.isNativePlatform && cap.isNativePlatform()) {
          const [deviceInfoData, appInfo] = await Promise.all([
            Device.getInfo(),
            CapacitorApp.getInfo(),
          ]);
          deviceInfo = {
            ...deviceInfo,
            platform: deviceInfoData.platform || deviceInfoData.operatingSystem,
            deviceModel: deviceInfoData.model,
            deviceOsVersion: deviceInfoData.osVersion,
            deviceManufacturer: deviceInfoData.manufacturer,
            deviceName: deviceInfoData.name,
            appVersion: appInfo.version,
          };
        } else if (navigator?.platform) {
          deviceInfo = {
            ...deviceInfo,
            platform: navigator.platform,
            appVersion: navigator.appVersion,
          };
        }
      } catch (e) {
        console.warn("Could not get device/app info for report:", e);
      }

      // Call Cloud Function to handle report submission
      await reportActivityCallable({
        activityId: activity.id,
        reason,
        deviceInfo: deviceInfo as any,
      });

      addToast(
        "Activity reported successfully. Thank you for helping keep our community safe.",
        "success",
      );
      setIsReportModalOpen(false);
    } catch (error) {
      crashlytics.logError(
        `Error reporting activity: ${JSON.stringify(error?.message)}`,
        "ActivityPage.tsx",
        error,
      );
      addToast("Failed to report activity. Please try again.", "error");
    }
  };

  const formatDateTime = (dateTime: string) => {
    const date = toJsDate(dateTime);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };


  // Check if user has access to private activity
  if (activity.isPrivate && currentUser) {
    const isCreator = activity.creator.id === currentUser.id;
    const isParticipant = activity.participantIds?.includes(currentUser.id);
    const isInvited =
      activity.invitedUserIds?.includes(currentUser.id) ||
      activity.pendingInvites?.includes(currentUser.id);

    if (!isCreator && !isParticipant && !isInvited) {
      return (
        <div className="bg-white min-h-screen">
          <div className="w-full max-w-md md:max-w-2xl lg:max-w-4xl mx-auto">
            <div className="flex items-center p-4 md:p-6 border-b border-gray-200">
              <button
                onClick={() => navigate(-1)}
                className="text-gray-600 mr-4"
              >
                <ArrowLeftIcon className="w-6 h-6" />
              </button>
              <h1 className="text-lg md:text-xl font-semibold">Activity</h1>
            </div>
            <div className="p-4 md:p-8 text-center">
              <div className="max-w-md mx-auto">
                <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-6 mb-6">
                  <div className="text-purple-600 text-5xl mb-4">🔒</div>
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">
                    Private Activity
                  </h2>
                  <p className="text-gray-600 leading-relaxed">
                    This is a private activity. You need an invitation from the
                    organizer to view or join.
                  </p>
                </div>
                <button
                  onClick={() => navigate("/")}
                  className="bg-primary text-white px-6 py-2 rounded-full hover:bg-primary/90 transition-colors"
                >
                  Back to Activities
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }
  }

  // Show 'Under Review' message for non-creators only (block access)
  if (activity.isReported && !isCreator) {
    return (
      <div className="bg-white min-h-screen">
        <div className="w-full max-w-md md:max-w-2xl lg:max-w-4xl mx-auto">
          <div className="flex items-center p-4 md:p-6 border-b border-gray-200">
            <button onClick={() => navigate(-1)} className="text-gray-600 mr-4">
              <ArrowLeftIcon className="w-6 h-6" />
            </button>
            <h1 className="text-lg md:text-xl font-semibold">Activity</h1>
          </div>
          <div className="p-4 md:p-8 text-center">
            <div className="max-w-md mx-auto">
              <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-6 mb-6">
                <div className="text-yellow-600 text-5xl mb-4">⚠️</div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">
                  Activity Under Review
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  This activity has been reported and is currently under review
                  by our team.
                </p>
                <p className="text-gray-600 leading-relaxed mt-3">
                  It will be available to view and participate after the review
                  is complete.
                </p>
              </div>
              <button
                onClick={() => navigate("/")}
                className="bg-primary text-white px-6 py-2 rounded-full hover:bg-primary/90 transition-colors"
              >
                Back to Activities
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white h-full flex flex-col">
      {/* Responsive container: mobile-first with larger breakpoints */}
      <div className="w-full max-w-md md:max-w-2xl lg:max-w-4xl mx-auto h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center">
            <button
              onClick={() => navigate(-1)}
              className="text-gray-600 mr-3 md:mr-4"
            >
              <ArrowLeftIcon className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-2">
              <h1 className="text-lg md:text-xl font-semibold">Activity</h1>
              {activity.isPrivate && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  🔒 Private
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-1 md:space-x-2">
            <button
              onClick={handleManualRefresh}
              disabled={isRefreshing}
              className={`text-gray-600 hover:text-primary p-2 transition-all duration-200 ${isRefreshing ? "animate-spin" : ""}`}
              title="Refresh activity"
            >
              <RefreshCwIcon className="w-5 h-5" />
            </button>
            {!isCreator && !activity.isDeleted && (
              <button
                onClick={() => setIsReportModalOpen(true)}
                className="px-2 py-1 rounded-full bg-red-100 text-xs text-red-600 hover:bg-red-200 transition-colors font-semibold"
                style={{
                  minWidth: 0,
                  height: "28px",
                  fontSize: "0.85rem",
                  lineHeight: 1,
                }}
                title="Report this activity"
              >
                Report
              </button>
            )}
            {isCreator &&
              !isExpired &&
              !activity.isDeleted &&
              !activity.isReported && (
                <>
                  <button
                    onClick={() => setShowInviteModal(true)}
                    disabled={isFull}
                    className={`p-2 transition-colors ${isFull ? "text-gray-400 cursor-not-allowed" : "text-gray-600 hover:text-primary"}`}
                    title={
                      isFull ? "Activity is full" : "Invite users to activity"
                    }
                  >
                    <UserPlusIcon className="w-6 h-5" />
                  </button>
                  <button
                    onClick={handleEdit}
                    className="text-gray-600 hover:text-primary p-2 transition-colors"
                    title="Edit activity"
                  >
                    <Edit2Icon className="w-5 h-5" />
                  </button>
                  <button
                    onClick={handleDelete}
                    className="text-red-600 hover:text-red-700 p-2 transition-colors"
                    title="Delete activity"
                  >
                    <Trash2Icon className="w-5 h-5" />
                  </button>
                </>
              )}
          </div>
        </div>

        {/* Scrollable Content Container - responsive with larger screens */}
        <div
          className="flex-1 overflow-y-auto pb-20 md:pb-6"
          onTouchStart={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
        >
          {/* Show warning banner for creators when activity is reported */}
          {activity.isReported && isCreator && (
            <div className="p-4 md:p-6 bg-yellow-50 border-b-4 border-yellow-400">
              <div className="max-w-3xl mx-auto">
                <div className="flex items-start">
                  <div className="text-yellow-600 text-3xl mr-4">⚠️</div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      Your Activity is Under Review
                    </h3>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      Your activity has been reported and is currently under
                      review by our team. You can view your activity details
                      below, but editing and participation are temporarily
                      disabled during the review process.
                    </p>
                    <p className="text-sm text-gray-700 leading-relaxed mt-2">
                      We will notify you once the review is complete. If you
                      have any questions, please contact our support team.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Show invitation banner for users with pending invites */}
          {hasPendingInvite && !isParticipant && (
            <div className="p-4 md:p-6 bg-purple-50 border-b-4 border-purple-400">
              <div className="max-w-3xl mx-auto">
                <div className="flex items-start">
                  <div className="text-purple-600 text-3xl mr-4">✉️</div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      You're Invited!
                    </h3>
                    <p className="text-sm text-gray-700 leading-relaxed mb-4">
                      <strong>{activity.creator.username}</strong> has invited
                      you to join this private activity. Accept the invitation
                      to become a participant and join the group chat.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <button
                        onClick={handleAcceptInvite}
                        disabled={isRespondingToInvite || isFull}
                        className="flex-1 sm:flex-none bg-primary text-white font-bold py-2.5 px-6 rounded-full hover:bg-primary/90 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
                      >
                        {isRespondingToInvite ? (
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        ) : null}
                        {isFull ? "Activity Full" : "Accept Invitation"}
                      </button>
                      <button
                        onClick={handleDeclineInvite}
                        disabled={isRespondingToInvite}
                        className="flex-1 sm:flex-none bg-gray-200 text-gray-700 font-bold py-2.5 px-6 rounded-full hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                      >
                        Decline
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Main Content Layout - responsive grid for larger screens */}
          <div className="md:grid md:grid-cols-3 md:gap-6 lg:gap-8">
            {/* Left Column: Activity Card and Actions */}
            <div className="md:col-span-2">
              {/* Activity Card */}
              <div className="p-4 md:p-6">
                <ActivityCard
                  activity={activity}
                  hideActions={true}
                  hideDescription={true}
                  truncateTitle={false}
                  viewMode="full"
                />
              </div>

              {/* Action Buttons */}
              {!activity.isDeleted && !activity.isReported && (
                <div className="p-4 md:px-6 border-b border-gray-200 md:border-b-0">
                  {isParticipant ? (
                    <div className="space-y-3 md:flex md:space-y-0 md:space-x-4">
                      {activity.participants.length > 1 && (
                        <button
                          onClick={handleChat}
                          className="w-full md:flex-1 bg-primary text-white font-bold py-3 px-6 rounded-full flex items-center justify-center hover:bg-primary/90 transition-colors"
                        >
                          <MessageSquareIcon className="w-5 h-5 mr-2" />
                          {isExpired ? "View Chat History" : "Group Chat"}
                        </button>
                      )}
                      {!isCreator && !isExpired && (
                        <button
                          onClick={handleLeave}
                          className="w-full md:flex-1 bg-red-600 text-white font-bold py-3 px-6 rounded-full flex items-center justify-center hover:bg-red-700 transition-colors"
                        >
                          <LogOutIcon className="w-5 h-5 mr-2" />
                          Leave Activity
                        </button>
                      )}
                    </div>
                  ) : hasPendingInvite ? (
                    <div className="text-center py-3">
                      <p className="text-gray-600 text-sm">
                        Please respond to your invitation above to join this
                        activity.
                      </p>
                    </div>
                  ) : (
                    <button
                      onClick={handleJoin}
                      disabled={
                        isFull ||
                        isJoining ||
                        activity.isReported ||
                        hasPendingRejoinRequest
                      }
                      className={`w-full font-bold py-3 px-6 rounded-full flex items-center justify-center disabled:cursor-not-allowed transition-colors ${
                        hasPendingRejoinRequest
                          ? "bg-yellow-500 text-white"
                          : "bg-primary text-white hover:bg-primary/90 disabled:bg-gray-400"
                      }`}
                    >
                      {isJoining ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      ) : (
                        <UserPlusIcon className="w-6 h-5 mr-2" />
                      )}
                      {hasPendingRejoinRequest
                        ? "Approval Pending"
                        : activity.isReported
                          ? "Activity Under Review"
                          : isFull
                            ? "Activity Full"
                            : isJoining
                              ? "Joining..."
                              : "Join Activity"}
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Right Column: Quick Info (desktop only) */}
            <div className="hidden md:block md:col-span-1">
              <div className="p-6 bg-gray-50 rounded-lg m-4 md:m-6 space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Quick Info
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <MapPinIcon className="w-4 h-4 mr-3 text-primary flex-shrink-0" />
                    <span className="text-gray-900 text-sm">
                      {activity.location}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <CalendarIcon className="w-4 h-4 mr-3 text-primary flex-shrink-0" />
                    <span className="text-gray-900 text-sm">
                      {formatDateTime(activity.dateTime)}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <ClockIcon className="w-4 h-4 mr-3 text-primary flex-shrink-0" />
                    <span className="text-gray-900 text-sm">
                      {activity.duration} minutes
                    </span>
                  </div>
                  <div className="flex items-center">
                    <UsersIcon className="w-4 h-4 mr-3 text-primary flex-shrink-0" />
                    <span className="text-gray-900 text-sm">
                      {activity.participants.length} / {activity.groupSize}{" "}
                      people
                    </span>
                  </div>
                </div>
                <button
                  onClick={handleShare}
                  className="w-full bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg flex items-center justify-center hover:bg-gray-300 transition-colors"
                >
                  <LinkIcon className="w-4 h-4 mr-2" />
                  Share Activity
                </button>
              </div>
            </div>
          </div>

          {/* Section Tabs - Responsive layout */}
          <div
            ref={contentRef}
            className="flex border-b border-gray-200 bg-white sticky top-0 z-10"
          >
            <button
              onClick={() => setActiveSection("details")}
              className={`flex-1 py-3 px-2 text-center font-medium text-sm md:text-base ${
                activeSection === "details"
                  ? "text-primary border-b-2 border-primary"
                  : "text-gray-500"
              }`}
            >
              <span className="hidden sm:inline">Details</span>
              <span className="sm:hidden">Info</span>
            </button>
            <button
              onClick={() => setActiveSection("participants")}
              className={`flex-1 py-3 px-2 text-center font-medium text-sm md:text-base ${
                activeSection === "participants"
                  ? "text-primary border-b-2 border-primary"
                  : "text-gray-500"
              }`}
            >
              <span className="hidden sm:inline">
                Participants ({activity.participants.length})
              </span>
              <span className="sm:hidden">
                People ({activity.participants.length})
              </span>
            </button>
            <button
              onClick={() => setActiveSection("activity")}
              className={`flex-1 py-3 px-2 text-center font-medium text-sm md:text-base ${
                activeSection === "activity"
                  ? "text-primary border-b-2 border-primary"
                  : "text-gray-500"
              }`}
            >
              <span className="hidden sm:inline">
                History ({auditLogs.length})
              </span>
              <span className="sm:hidden">Log ({auditLogs.length})</span>
            </button>
          </div>

          {/* Content Sections - Responsive padding */}
          <div className="p-4 md:p-6">
            {activeSection === "details" && (
              <div className="space-y-4 md:space-y-6">
                <div>
                  <h3 className="text-lg md:text-xl font-semibold mb-2 md:mb-3">
                    Description
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-sm md:text-base">
                    <ActivityRichText
                      text={activity.description}
                      emptyText="No description provided."
                    />
                  </p>
                </div>

                {/* Mobile: Show details here, Desktop: Details are in sidebar */}
                <div className="space-y-3 md:hidden">
                  <div className="flex items-center">
                    <MapPinIcon className="w-5 h-5 mr-3 text-primary" />
                    <span className="text-gray-900">{activity.location}</span>
                  </div>
                  <div className="flex items-center">
                    <CalendarIcon className="w-5 h-5 mr-3 text-primary" />
                    <span className="text-gray-900">
                      {formatDateTime(activity.dateTime)}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <ClockIcon className="w-5 h-5 mr-3 text-primary" />
                    <span className="text-gray-900">
                      {activity.duration} minutes
                    </span>
                  </div>
                  <div className="flex items-center">
                    <UsersIcon className="w-5 h-5 mr-3 text-primary" />
                    <span className="text-gray-900">
                      {activity.participants.length} / {activity.groupSize}{" "}
                      people
                    </span>
                  </div>
                </div>

                {/* Share button for mobile */}
                <div className="md:hidden">
                  <button
                    onClick={handleShare}
                    className="w-full bg-gray-100 text-gray-700 font-medium py-3 px-4 rounded-lg flex items-center justify-center hover:bg-gray-200 transition-colors"
                  >
                    <LinkIcon className="w-5 h-5 mr-2" />
                    Share Activity
                  </button>
                </div>
              </div>
            )}

            {activeSection === "participants" && (
              <div className="space-y-3 md:space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg md:text-xl font-semibold">
                    Participants
                  </h3>
                  <span className="text-sm text-gray-500">
                    {activity.participants.length} / {activity.groupSize}
                  </span>
                </div>

                {/* Responsive grid for participants */}
                <div className="space-y-3 md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-4 md:space-y-0">
                  {activity.participants.map((participant, idx) => (
                    <div
                      key={participant.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div
                        className="flex items-center flex-1 min-w-0 cursor-pointer hover:bg-gray-100 rounded-lg transition-colors"
                        onClick={() => navigate(`/profile/${participant.id}`)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ")
                            navigate(`/profile/${participant.id}`);
                        }}
                      >
                        <img
                          src={participantImages[idx]}
                          alt={participant.username}
                          className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover mr-3 flex-shrink-0"
                        />
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 text-sm md:text-base truncate">
                            {participant.username}
                          </p>
                          {participant.id === activity.creator.id && (
                            <span className="text-xs text-primary">
                              Organizer
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 flex-shrink-0">
                        <button
                          onClick={() => navigate(`/profile/${participant.id}`)}
                          className="text-primary text-xs md:text-sm font-medium hover:underline"
                        >
                          <span className="hidden sm:inline">View Profile</span>
                          <span className="sm:hidden">View</span>
                        </button>
                        {isCreator &&
                          participant.id !== activity.creator.id &&
                          !activity.isReported && (
                            <button
                              onClick={() =>
                                handleKickParticipant(
                                  participant.id,
                                  participant.username,
                                )
                              }
                              className="text-red-600 hover:text-red-700 p-1 rounded transition-colors"
                              title={`Remove ${participant.username} from activity`}
                            >
                              <UserXIcon className="w-4 h-4" />
                            </button>
                          )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pending Invites (Only visible to organizer) */}
                {isCreator && pendingInviteUsers.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h3 className="text-lg md:text-xl font-semibold mb-4">
                      Pending Invitations
                    </h3>
                    <div className="space-y-3">
                      {pendingInviteUsers.map((user, index) => (
                        <div
                          key={user.id}
                          className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg"
                        >
                          <div
                            className="flex items-center flex-1 min-w-0 cursor-pointer hover:bg-blue-100 rounded-lg transition-colors"
                            onClick={() => navigate(`/profile/${user.id}`)}
                            role="button"
                            tabIndex={0}
                          >
                            <img
                              src={
                                pendingInviteImages[index] ||
                                user.profileImageUrl
                              }
                              alt={user.username}
                              className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover mr-3 flex-shrink-0"
                            />
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900 text-sm md:text-base truncate">
                                {user.username}
                              </p>
                              <span className="text-xs text-blue-700">
                                Invited
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 flex-shrink-0">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setConfirmModal({
                                  isOpen: true,
                                  title: "Cancel Invitation",
                                  message: `Cancel invitation for ${user.username}?`,
                                  confirmText: "Cancel Invite",
                                  confirmStyle: "danger",
                                  onConfirm: async () => {
                                    try {
                                      await cancelInvite(activity.id, user.id);
                                    } catch (error) {
                                      crashlytics.logError(
                                        `Error canceling invite for ${user.id}: ${JSON.stringify(error?.message)}`,
                                        "ActivityPage.tsx",
                                        error,
                                      );
                                      addToast(
                                        "Failed to cancel invitation.",
                                        "error",
                                      );
                                    }
                                    setConfirmModal((prev) => ({
                                      ...prev,
                                      isOpen: false,
                                    }));
                                  },
                                });
                              }}
                              className="text-red-600 hover:text-red-700 text-xs md:text-sm font-medium px-3 py-1 rounded-lg border border-red-600 hover:bg-red-50 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Pending Rejoin Requests (Only visible to organizer) */}
                {isCreator && pendingRejoinUsers.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h3 className="text-lg md:text-xl font-semibold mb-4">
                      Pending Rejoin Requests
                    </h3>
                    <div className="space-y-3">
                      {pendingRejoinUsers.map((user, index) => (
                        <div
                          key={user.id}
                          className="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-lg"
                        >
                          <div
                            className="flex items-center flex-1 min-w-0 cursor-pointer hover:bg-yellow-100 rounded-lg transition-colors"
                            onClick={() => navigate(`/profile/${user.id}`)}
                            role="button"
                            tabIndex={0}
                          >
                            <img
                              src={
                                pendingRejoinImages[index] ||
                                user.profileImageUrl
                              }
                              alt={user.username}
                              className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover mr-3 flex-shrink-0"
                            />
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900 text-sm md:text-base truncate">
                                {user.username}
                              </p>
                              <span className="text-xs text-yellow-700">
                                Wants to rejoin
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 flex-shrink-0">
                            <button
                              onClick={async () => {
                                try {
                                  await approveRejoinRequest(
                                    activity.id,
                                    user.id,
                                  );
                                } catch (error) {
                                  addToast(
                                    "Failed to approve request",
                                    "error",
                                  );
                                }
                              }}
                              className="bg-primary text-white text-xs md:text-sm font-medium px-3 py-1.5 rounded-lg hover:opacity-90 transition-opacity"
                            >
                              Approve
                            </button>
                            <button
                              onClick={async () => {
                                try {
                                  await declineRejoinRequest(
                                    activity.id,
                                    user.id,
                                  );
                                } catch (error) {
                                  addToast(
                                    "Failed to decline request",
                                    "error",
                                  );
                                }
                              }}
                              className="bg-gray-200 text-gray-800 text-xs md:text-sm font-medium px-3 py-1.5 rounded-lg hover:bg-gray-300 transition-colors"
                            >
                              Decline
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeSection === "activity" && (
              <div className="space-y-3 md:space-y-4">
                <h3 className="text-lg md:text-xl font-semibold">
                  Activity History
                </h3>
                {auditLogs.length === 0 ? (
                  <p className="text-gray-500 text-center py-4 md:py-8">
                    No activity history yet
                  </p>
                ) : (
                  <div className="space-y-2 md:space-y-3">
                    {auditLogs.map((log) => (
                      <AuditLogItem key={log.id} log={log} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {(activity.isDeleted || isExpired) && (
            <div className="p-4 md:p-6 border-t border-gray-200">
              <div className="text-center bg-gray-100 text-gray-500 font-semibold py-3 px-6 rounded-full">
                {activity.isDeleted ? "Activity Deleted" : "Activity Ended"}
              </div>
            </div>
          )}
        </div>
        {/* End of scrollable content */}
      </div>
      {/* End of main container */}

      {/* Edit Activity Modal */}
      {showEditModal && activity && (
        <EditActivityModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          activity={activity}
          onSave={handleSaveEdit}
        />
      )}

      {/* Invite Users Modal */}
      {showInviteModal && activity && (
        <InviteUsersModal
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          onInvite={handleInvite}
          activityId={activity.id}
          alreadyInvitedIds={[
            ...(activity.invitedUserIds || []),
            ...(activity.pendingInvites || []),
          ]}
          participantIds={activity.participantIds || []}
          isFull={isFull}
        />
      )}

      {/* Report Activity Modal */}
      <Portal>
        <ReportActivityModal
          isOpen={isReportModalOpen}
          onClose={() => setIsReportModalOpen(false)}
          onSubmit={handleReportSubmit}
          activityTitle={activity?.title || ""}
          activityId={activity?.id || ""}
          reporterEmail={currentUser?.email || ""}
          reporterUsername={currentUser?.username || ""}
        />
      </Portal>

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        confirmStyle={confirmModal.confirmStyle}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};

export default ActivityPage;
